import { fieldCannotBeBlankErrorMessage } from '@ben/columbia/components/shared/constants';
import Input from '@ben/orion/input';
import RadioButton from '@ben/orion/radio-button';
import Typography, { TypographyVariant } from '@ben/orion/typography';
import { BuySellHolidayUnitType } from '@ben/shared/types/columbia';
import { Controller, useFormContext } from 'react-hook-form';
import styled, { css } from 'styled-components';
import useMinMaxInput from '../../../../hooks/use-min-max-input';
import MinMaxConfiguration from '../_common/min-max-configuration';

interface IModularBuyAndSellHolidayInputProps {
  componentControllerName: string;
}

const ModularBuyAndSellHolidayInput = ({
  componentControllerName,
}: IModularBuyAndSellHolidayInputProps) => {
  const form = useFormContext();
  const { control } = form;

  const { isFixedNumber, setIsFixedNumber, renderMinMaxInput } = useMinMaxInput(
    {
      componentControllerName,
      form,
      validationMessage: {
        min: 'Min number of days should be less than max number of days',
        max: 'Max number of days should be greater than min number of days',
      },
    }
  );

  return (
    <Wrapper>
      <UnitWrapper>
        <Typography variant={TypographyVariant.bodySm2}>Time unit</Typography>
        <RowWrapper>
          <Controller
            control={control}
            name={`${componentControllerName}.unitType`}
            render={({ field }) => (
              <>
                <RadioButton
                  label="Days"
                  checked={field.value === BuySellHolidayUnitType.DAYS}
                  onChange={() => field.onChange(BuySellHolidayUnitType.DAYS)}
                  name={field.name}
                />
                <RadioButton
                  label="Hours"
                  checked={field.value === BuySellHolidayUnitType.HOURS}
                  onChange={() => field.onChange(BuySellHolidayUnitType.HOURS)}
                  name={field.name}
                />
              </>
            )}
          />
        </RowWrapper>
      </UnitWrapper>
      <RowWrapper>
        <Controller
          control={control}
          name={`${componentControllerName}.default`}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Default value"
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              type="number"
            />
          )}
          rules={{
            required: fieldCannotBeBlankErrorMessage,
            validate: (value) => {
              if (!value || !isFixedNumber) return true;
              const min = form.watch(`${componentControllerName}.min`);
              const max = form.watch(`${componentControllerName}.max`);
              if (
                min &&
                max &&
                (Number(value) < Number(min) || Number(value) > Number(max))
              ) {
                return 'Default holidays should be between min and max values';
              }
              return true;
            },
          }}
        />
        <Controller
          control={control}
          name={`${componentControllerName}.interval`}
          render={({ field, fieldState }) => (
            <Input
              {...field}
              name="Interval increments"
              hasError={!!fieldState.error}
              errorMessage={fieldState.error?.message}
              type="number"
              hintText="Specifies how much values change between points. For example, an increment of 0.5 means values change by 0.5 units per step. The most common interval is 1."
            />
          )}
        />
      </RowWrapper>
      <MinMaxConfiguration
        isFixedNumber={isFixedNumber}
        setIsFixedNumber={setIsFixedNumber}
      />
      <RowWrapper>
        {renderMinMaxInput({
          isMin: true,
          hintText: isFixedNumber
            ? 'Input this value as negative number (e.g. -5)'
            : undefined,
          name: 'Max number to sell',
        })}
        {renderMinMaxInput({
          isMin: false,
          name: 'Max number to buy',
        })}
      </RowWrapper>
    </Wrapper>
  );
};

export default ModularBuyAndSellHolidayInput;

const Wrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;

const RowWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(2)};
  `}
`;

const UnitWrapper = styled.div`
  ${({ theme }) => css`
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(2)};
  `}
`;
